import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function PageHeader({ eyebrow, title, desc, children }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6 fade-up">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#03AB9E] mb-2">{eyebrow}</p>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">{title}</h1>
        {desc && <p className="text-sm text-slate-400 mt-2 max-w-2xl">{desc}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function Panel({ title, sub, right, children, className = "", testId }) {
  return (
    <section
      data-testid={testId}
      className={`bg-[#0F172A]/80 backdrop-blur-sm border border-slate-800 rounded-lg p-5 md:p-6 fade-up ${className}`}
    >
      {(title || right) && (
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-base font-semibold tracking-tight">{title}</h3>
            {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
          </div>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

export function Kpi({ label, value, unit, delta, accent = "#1074BA", testId }) {
  return (
    <div data-testid={testId} className="relative bg-[#0F172A]/80 border border-slate-800 rounded-lg p-5 overflow-hidden fade-up hover:border-slate-700 transition-colors">
      <span className="absolute left-0 top-0 h-full w-1" style={{ background: accent }} />
      <p className="text-[11px] font-medium text-slate-500 uppercase tracking-widest">{label}</p>
      <p className="font-mono text-2xl md:text-3xl font-bold tracking-tight mt-2">
        {value}
        {unit && <span className="text-sm text-slate-500 ml-1.5 font-medium">{unit}</span>}
      </p>
      {delta != null && (
        <p className={`font-mono text-xs mt-1.5 ${delta >= 0 ? "text-[#03AB9E]" : "text-red-400"}`}>
          {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%
        </p>
      )}
    </div>
  );
}

export function FilterSelect({ value, onChange, options, placeholder, testId, allLabel = "All" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="w-[170px] h-9 bg-slate-900 border-slate-700 text-sm">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-slate-900 border-slate-700">
        {allLabel && <SelectItem value="all">{allLabel}</SelectItem>}
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ChartTooltip({ active, payload, label, unit = "", digits = 0 }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-950/95 border border-slate-700 rounded-md px-3 py-2 shadow-xl">
      <p className="font-mono text-xs text-slate-400 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="text-xs flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.payload?.fill }} />
          <span className="text-slate-300">{p.name}</span>
          <span className="font-mono ml-auto pl-3 text-white">
            {Number(p.value).toLocaleString("en-US", { maximumFractionDigits: digits })} {unit}
          </span>
        </p>
      ))}
    </div>
  );
}

export function Segmented({ value, onChange, options, testId }) {
  return (
    <div data-testid={testId} className="inline-flex p-1 rounded-md bg-slate-900 border border-slate-700">
      {options.map((o) => (
        <button
          key={o.value}
          data-testid={`${testId}-${o.value}`}
          onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded transition-colors ${
            value === o.value ? "bg-[#1074BA] text-white" : "text-slate-400 hover:text-white"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Loading() {
  return <div className="py-24 text-center font-mono text-xs text-slate-500 animate-pulse">LOADING DATA…</div>;
}
